import React from 'react';
import Spinner from '../Spinner';
import {
  AdminInvoiceAnalyticsKpis,
  AdminInvoiceAnalyticsOpenNow,
} from '../../types/adminInvoice';
import { formatInvoiceMoney, isAnalyticsPeriodEmpty } from '../../utils/admin/formatInvoiceMoney';

interface InvoiceAnalyticsSummaryCardProps {
  loading: boolean;
  error: string | null;
  currency: string;
  kpis: AdminInvoiceAnalyticsKpis | null;
  openNow: AdminInvoiceAnalyticsOpenNow | null;
  onDetails: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}

const InvoiceAnalyticsSummaryCard: React.FC<InvoiceAnalyticsSummaryCardProps> = ({
  loading,
  error,
  currency,
  kpis,
  openNow,
  onDetails,
  onRetry,
  onDismiss,
}) => {
  const overdueCount = openNow?.overdue_count ?? 0;
  const hasOverdue = overdueCount > 0;
  const emptyRange = kpis != null && isAnalyticsPeriodEmpty('all', kpis);

  return (
    <div className="inline-flex items-center gap-0.5">
      <button
        type="button"
        onClick={error && !kpis ? onRetry : onDetails}
        title="Invoice analytics"
        className="inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="font-medium text-gray-600 shrink-0">Billing</span>

        {loading && !kpis && (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <Spinner size="xs" />
          </span>
        )}

        {error && !loading && !kpis && (
          <span className="text-red-700">Couldn&apos;t load · Retry</span>
        )}

        {kpis && emptyRange && (
          <span className="text-gray-500">No payments</span>
        )}

        {kpis && !emptyRange && (
          <span className="tabular-nums text-gray-900">
            {formatInvoiceMoney(kpis.collected_amount, currency)}
            <span className="text-gray-400"> / </span>
            {formatInvoiceMoney(kpis.invoiced_amount, currency)}
          </span>
        )}

        {hasOverdue && (
          <span className="inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {overdueCount} overdue
          </span>
        )}
      </button>

      <button
        type="button"
        aria-label="Dismiss billing summary"
        title="Dismiss for this session"
        onClick={onDismiss}
        className="p-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span aria-hidden className="text-sm leading-none px-1">×</span>
      </button>
    </div>
  );
};

export default InvoiceAnalyticsSummaryCard;
